// File: frontend/src/utils/accountScope.js
//
// ONE definition of "what belongs to the signed-in account" and how to destroy
// it, plus the authentication epoch that lets in-flight work recognise that it
// now belongs to a previous account.
//
// WHY THIS EXISTS
// ---------------
// A P0 cross-account exposure: after logging out of Account A and into Account
// B, the dashboard painted Account A's sales, customer names, phone numbers and
// wallet balance. Three things combined to cause it:
//
//   1. The dashboard cached its whole payload in sessionStorage under a key made
//      of routerId + date range — no tenant, no user. Two accounts produced the
//      SAME key.
//   2. Logout removed only token / refresh_token / user. The cached payload and
//      the selected router survived into the next account.
//   3. Login is an SPA navigation, so providers above <Router> never remount and
//      kept the previous account's state.
//
// The fix is structural: every account-scoped browser value is created through
// this module, and `clearAccountScope()` is the single thing logout has to call.
// Adding a new cached value in future cannot reintroduce the bug as long as its
// key is prefixed here.

/* ── Keys that belong to the signed-in ACCOUNT (destroyed on logout) ───────── */
export const ACCOUNT_LOCAL_KEYS = [
  "token",
  "refresh_token",
  "user",
  "selectedRouterId",
  "voucherSearchPresets",   // user-authored filters — may name another tenant's routers
  "router_thresholds",      // per-tenant router alerting thresholds
];

/** sessionStorage/localStorage prefixes whose every key is account-scoped. */
export const ACCOUNT_KEY_PREFIXES = [
  "pesafii:dash:cc:",       // dashboard command-center payload cache (sessionStorage)
  // The persisted last-good KPI snapshot. It lives in localStorage so it can
  // survive a tab close, which is exactly why it MUST be listed here: unlike the
  // session cache above, closing the browser no longer disposes of it, so
  // logging out is the only thing that can. Leaving one operator's revenue on a
  // shared machine for the next person to sign in is not an acceptable price for
  // a fast first paint.
  "pesafii:dash-core:",
  "pesafii:acct:",          // reserved for future account-scoped caches
];

/* ── Keys that are deliberately GLOBAL and must SURVIVE a logout ──────────── */
// themeMode      — appearance preference, carries no tenant information
// device_id      — stable device identifier used for refresh-token binding
// router_obs_range — a chart window ("1h"), not tenant data
export const GLOBAL_KEYS = ["themeMode", "device_id", "router_obs_range"];

function removeByPrefix(storage) {
  if (!storage) return;
  try {
    const doomed = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && ACCOUNT_KEY_PREFIXES.some((p) => k.startsWith(p))) doomed.push(k);
    }
    doomed.forEach((k) => storage.removeItem(k));
  } catch (_) { /* storage disabled — nothing to clear */ }
}

/**
 * Destroy every account-scoped browser value. Safe to call repeatedly and safe
 * to call when already signed out. Never throws: a storage failure must not be
 * able to abort a logout.
 */
export function clearAccountScope() {
  try {
    ACCOUNT_LOCAL_KEYS.forEach((k) => {
      try { localStorage.removeItem(k); } catch (_) {}
    });
    removeByPrefix(typeof sessionStorage !== "undefined" ? sessionStorage : null);
    removeByPrefix(typeof localStorage !== "undefined" ? localStorage : null);
  } catch (_) { /* never block logout */ }
}

/* ── Authentication epoch ─────────────────────────────────────────────────────
   A monotonically increasing generation number for "who is signed in".

   Any async work that will write into shared state captures the epoch when it
   STARTS and re-checks it before applying its result. A response that belongs to
   a previous account is dropped instead of overwriting the current one. This is
   what stops an Account A request that resolves after Account B has logged in.

   It lives in a module variable, not storage: it must reset to 0 on a full
   document load, because a document load already discards all in-flight work. */
let _authEpoch = 0;
const _epochListeners = new Set();

export function getAuthEpoch() { return _authEpoch; }

/** Bump the epoch. Called on login, logout and any session invalidation. */
export function bumpAuthEpoch() {
  _authEpoch += 1;
  _epochListeners.forEach((fn) => { try { fn(_authEpoch); } catch (_) {} });
  return _authEpoch;
}

/** Subscribe to epoch changes. Returns an unsubscribe function. */
export function onAuthEpochChange(fn) {
  _epochListeners.add(fn);
  return () => _epochListeners.delete(fn);
}

/** True when `epoch` is still the current account generation. */
export function isCurrentEpoch(epoch) { return epoch === _authEpoch; }

/* ── Identity of the signed-in account, for cache namespacing ─────────────── */
/**
 * { tenantId, userId } for the signed-in account, or nulls when signed out or
 * when the stored user is unusable. Callers MUST treat a null as "do not touch
 * account-scoped caches" — never as a wildcard.
 */
export function getAccountIdentity() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { tenantId: null, userId: null };
    const u = JSON.parse(raw);
    const tenantId = u?.tenant_id ?? null;
    const userId = u?.id ?? null;
    return {
      tenantId: tenantId == null ? null : String(tenantId),
      userId: userId == null ? null : String(userId),
    };
  } catch (_) {
    return { tenantId: null, userId: null };
  }
}
